/**
 * Catalog.
 *
 * Master Specification section 12 requires Product and SKU/ProductVariant to be
 * separate concepts, and forbids two things explicitly:
 *
 *   > Não guardar estoque diretamente no Product como fonte de verdade.
 *   > Não guardar preço diretamente no Product como única fonte de verdade.
 *
 * So there is no `stock` and no `price` anywhere in this file. Stock is a
 * ledger of movements (section 14) and price is its own domain with lists,
 * tiers and rules (section 15). Putting either here as a convenience is the
 * shortcut that makes both of those features a rewrite instead of an addition.
 *
 * The Product/Variant split is here from the first migration for the same
 * reason. Inventory movements, sale lines and price rules all point at the
 * *variant* — the thing with a barcode that can be counted and sold. Shipping a
 * flat product table now would mean migrating every one of those foreign keys
 * later, at the point when there are most of them.
 *
 * Today the interface presents one variant per product and never says the word
 * "variação". The model is right; the screen is simple.
 */
export const CATALOG_STATUSES = ['active', 'archived'];
/**
 * Units of measure.
 *
 * A fixed list rather than a table, because a unit table earns its place when
 * conversions arrive (a box of twelve, a metre of cable sold in centimetres),
 * and conversions are a feature with rules of their own. Until then a table
 * would be a table with no behaviour.
 *
 * `decimals` is the reason this is not just a list of strings. Master
 * Specification section 9 is explicit that quantity is not an integer — 1.5 kg
 * and 0.75 m are normal — but half a unit of a physical item is not, and an
 * inventory ledger that accepts 0.5 UN will eventually produce a count nobody
 * can explain.
 */
export const UNITS = {
    UN: { label: 'Unidade', decimals: 0 },
    PC: { label: 'Peça', decimals: 0 },
    CX: { label: 'Caixa', decimals: 0 },
    PAR: { label: 'Par', decimals: 0 },
    KIT: { label: 'Kit', decimals: 0 },
    KG: { label: 'Quilograma', decimals: 3 },
    G: { label: 'Grama', decimals: 0 },
    L: { label: 'Litro', decimals: 3 },
    ML: { label: 'Mililitro', decimals: 0 },
    M: { label: 'Metro', decimals: 2 },
    CM: { label: 'Centímetro', decimals: 0 },
    M2: { label: 'Metro quadrado', decimals: 2 },
    M3: { label: 'Metro cúbico', decimals: 3 },
};
export const UNIT_CODES = Object.keys(UNITS);
export const isUnit = (value) => typeof value === 'string' && Object.hasOwn(UNITS, value);
/** How many decimal places a quantity in this unit may carry. */
export const decimalsForUnit = (unit) => UNITS[unit].decimals;
/**
 * SKUs are typed by people, read aloud over the phone and printed on labels.
 *
 * Normalising on the way in means `ab-100`, ` AB-100 ` and `Ab-100` are one
 * product rather than three, which is the difference between a uniqueness
 * constraint that protects the catalogue and one that only appears to.
 */
export const normalizeSku = (sku) => sku.trim().toUpperCase().replace(/\s+/g, '');
export const isValidSku = (sku) => /^[A-Z0-9][A-Z0-9._/-]{0,39}$/.test(normalizeSku(sku));
/**
 * A raiz de um código de cor — `003216-2` é a cor 2 da raiz `003216` (F181).
 *
 * O dono: *"o código do produto é 6455; se for a primeira cor do produto vai
 * criar assim 6455-1"*. Toda cor passa a carregar o seu número, a primeira
 * inclusive — e por isso o código da PADRÃO deixa de ser a raiz e vira
 * `raiz-1`. Alguém precisa saber, depois disso, de onde numerar a próxima.
 *
 * Não dá para tirar o `-N` de qualquer código: `CABO-2` pode ser o código que
 * o fornecedor manda, e ninguém numera a partir de `CABO`. A resposta vem do
 * conjunto: só é sufixo de cor o `-N` cuja raiz OUTRA cor do mesmo produto
 * também usa. Um produto de uma cor só nunca tem o código remexido.
 *
 * ```
 * skuRoot('003216',   [])                        → '003216'   (ainda não numerado)
 * skuRoot('003216-1', ['003216-2'])              → '003216'   (numerado)
 * skuRoot('CABO-2',   [])                        → 'CABO-2'   (código do fornecedor)
 * skuRoot('CABO-2-1', ['CABO-2-2'])              → 'CABO-2'   (raiz com hífen)
 * ```
 */
export const skuRoot = (defaultSku, siblingSkus) => {
    const match = /^(.+)-(\d+)$/.exec(defaultSku);
    if (match === null)
        return defaultSku;
    const root = match[1] ?? defaultSku;
    const numbered = siblingSkus.some((sku) => sku !== defaultSku && sku.startsWith(`${root}-`));
    return numbered ? root : defaultSku;
};
/** O código da enésima cor de uma raiz: `colourSku('6455', 1)` → `6455-1`. */
export const colourSku = (root, n) => `${root}-${String(n)}`;
/**
 * Os dígitos de um código, para virar código de barras interno (F133, F181).
 *
 * `000123` é `000123`; a cor `003216-2` é `0032162`, para o número embaixo das
 * barras continuar parecido com o código da peça. Só número e só o hífen da
 * cor: `CABO-HDMI` não vira código de barras nenhum, e `CABO-2` também não —
 * dali sairia `2`, um código de barras que não diz respeito a este produto.
 *
 * Nulo quando não serve, e aí quem chama cai para o próximo interno da loja.
 */
export const skuDigits = (sku) => {
    const match = /^(\d+)(?:-(\d+))?$/.exec(normalizeSku(sku));
    if (match === null)
        return null;
    const digits = (match[1] ?? '') + (match[2] ?? '');
    return digits.length > 11 ? null : digits;
};
/**
 * Um SKU sugerido, para o formulário abrir com o campo preenchido.
 *
 * O código interno não significa nada para quem cadastra — significa para o
 * sistema. Exigir que a pessoa invente um antes de salvar o primeiro produto é
 * cobrar uma decisão de quem não tem critério para tomá-la, e o resultado
 * previsível é um catálogo com `1`, `2`, `teste` e `asdf`. Quem tem código
 * próprio do fornecedor apaga e digita o dele; o campo continua editável.
 *
 * **O alfabeto não tem `0`, `1`, `I`, `L`, `O` nem `U`.** Os cinco primeiros
 * porque um SKU é lido em voz alta ao telefone, escrito à mão numa etiqueta e
 * digitado num caixa — e `PRD-I0L1` é um código que só existe direito na tela.
 * O `U` sai porque, com as outras letras, ele forma palavra feia em português
 * mais vezes do que se imagina num catálogo de milhares de itens.
 *
 * Aleatório e não sequencial: sequência exige um contador no banco e uma
 * decisão sobre o que acontece quando duas pessoas cadastram ao mesmo tempo.
 * Trinta caracteres em seis posições são 729 milhões de combinações — para o
 * catálogo de uma loja, a colisão não acontece; e se acontecer, o servidor
 * recusa dizendo qual código repetiu e a pessoa troca uma letra.
 *
 * Usa `crypto.getRandomValues` quando existe e cai para `Math.random()` quando
 * não — e a queda é aceitável AQUI porque este valor não guarda segredo
 * nenhum: ele só precisa não repetir dentro do catálogo de uma loja, e o
 * servidor recusa a repetição se ela acontecer. Num token de sessão ou numa
 * senha temporária a mesma queda seria uma falha de segurança, e por isso
 * `@aeris/auth` usa `node:crypto` sem alternativa.
 */
const SKU_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const SKU_SUGGESTION_PREFIX = 'PRD-';
const SKU_BODY_LENGTH = 6;
const randomBytes = (count) => {
    const bytes = new Uint8Array(count);
    const source = globalThis.crypto;
    if (source?.getRandomValues !== undefined) {
        source.getRandomValues(bytes);
        return bytes;
    }
    for (let i = 0; i < count; i += 1) {
        bytes[i] = Math.floor(Math.random() * 256);
    }
    return bytes;
};
export const generateSku = () => {
    let body = '';
    for (const byte of randomBytes(SKU_BODY_LENGTH)) {
        /*
         * 256 não é múltiplo de 30, então o resto favorece levemente as primeiras
         * letras do alfabeto. Para um código de catálogo isso não tem consequência
         * — está anotado para ninguém copiar esta linha para onde teria.
         */
        body += SKU_ALPHABET[byte % SKU_ALPHABET.length] ?? '';
    }
    return SKU_SUGGESTION_PREFIX + body;
};
/**
 * NCM — eight digits, no separators.
 *
 * Only the shape is checked. Whether `8471.30.12` is the *correct*
 * classification for a given product is a fiscal question with legal
 * consequences, answered against the official table by the fiscal feature.
 * Validating the format here and calling it "valid NCM" would be the more
 * dangerous of the two mistakes available.
 */
export const normalizeNcm = (ncm) => ncm.replace(/[\s.-]/g, '');
export const isValidNcmFormat = (ncm) => /^[0-9]{8}$/.test(normalizeNcm(ncm));
export const formatNcm = (ncm) => {
    const normalized = normalizeNcm(ncm);
    if (!isValidNcmFormat(normalized))
        return ncm;
    return `${normalized.slice(0, 4)}.${normalized.slice(4, 6)}.${normalized.slice(6)}`;
};
/** Trimmed, or null when there is nothing left. A blank string is not a value. */
export const optionalText = (value) => {
    if (value === null || value === undefined)
        return null;
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
};
/**
 * Como uma variação se chama no balcão, na nota e na etiqueta (F176).
 *
 * "Capa iPhone 15 — Preto". Sem nome, só o produto: o produto de uma variação
 * só aparece exatamente como sempre apareceu.
 */
export const variantDisplayName = (productName, variantName) => variantName === null || variantName === undefined || variantName === ''
    ? productName
    : `${productName} — ${variantName}`;
//# sourceMappingURL=entities.js.map